import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import MainNavigation from '../../components/ui/MainNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import CartItem from './components/CartItem';
import OrderSummary from './components/OrderSummary';
import SavedItems from './components/SavedItems';
import EmptyCart from './components/EmptyCart';
import BulkOrderSection from './components/BulkOrderSection';

const ShoppingCart = () => {
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [savedItems, setSavedItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSavedItems, setShowSavedItems] = useState(false);

  // Mock user data
  const user = {
    id: 1,
    name: "James Mukasa",
    email: "james.mukasa@company.co.ug",
    role: "customer"
  };

  // Mock cart data
  useEffect(() => {
    const mockCartItems = [
      {
        id: 1,
        name: "Enterprise Network Infrastructure Setup",
        category: "Infrastructure",
        price: 15000000,
        quantity: 1,
        image: "https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=400",
        specifications: "Complete network setup with enterprise-grade equipment, security protocols, and 24/7 monitoring",
        deliveryTime: "2-3 weeks"
      },
      {
        id: 2,
        name: "Cloud Migration Strategy & Implementation",
        category: "Consultancy",
        price: 8500000,
        quantity: 2,
        image: "https://images.pexels.com/photos/1181675/pexels-photo-1181675.jpeg?w=400",
        specifications: "Comprehensive cloud migration planning, implementation, and staff training",
        deliveryTime: "4-6 weeks"
      },
      {
        id: 3,
        name: "Cybersecurity Assessment & Implementation",
        category: "Security",
        price: 12000000,
        quantity: 1,
        image: "https://images.pixabay.com/photo/2018/05/14/16/54/cyber-3400789_1280.jpg?w=400",
        specifications: "Complete security audit, vulnerability assessment, and security system implementation",
        deliveryTime: "3-4 weeks"
      }
    ];

    const mockSavedItems = [
      {
        id: 4,
        name: "Custom Software Development",
        category: "Software",
        price: 25000000,
        image: "https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=400"
      },
      {
        id: 5,
        name: "IT Policy & Governance Framework",
        category: "Policy",
        price: 6000000,
        image: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?w=400"
      }
    ];

    setTimeout(() => {
      setCartItems(mockCartItems);
      setSavedItems(mockSavedItems);
      setIsLoading(false);
    }, 1000);
  }, []);

  // Calculate totals
  const subtotal = cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0);
  const tax = subtotal * 0.18; // 18% VAT
  const shipping = cartItems?.some(item => item?.category === 'Infrastructure') ? 500000 : 0;
  const discount = 0; // Will be updated when promo code is applied
  const total = subtotal + tax + shipping - discount;

  const handleUpdateQuantity = (itemId, newQuantity) => {
    setCartItems(items =>
      items?.map(item =>
        item?.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const handleRemoveItem = (itemId) => {
    setCartItems(items => items?.filter(item => item?.id !== itemId));
  };

  const handleSaveForLater = (itemId) => {
    const itemToSave = cartItems?.find(item => item?.id === itemId);
    if (itemToSave) {
      setSavedItems(items => [...items, { ...itemToSave, quantity: 1 }]);
      handleRemoveItem(itemId);
    }
  };

  const handleMoveToCart = (itemId) => {
    const itemToMove = savedItems?.find(item => item?.id === itemId);
    if (itemToMove) {
      setCartItems(items => [...items, { ...itemToMove, quantity: 1 }]);
      setSavedItems(items => items?.filter(item => item?.id !== itemId));
    }
  };

  const handleRemoveFromSaved = (itemId) => {
    setSavedItems(items => items?.filter(item => item?.id !== itemId));
  };

  const handleApplyPromoCode = (code) => {
    // Mock promo code application
    console.log('Applying promo code:', code);
  };

  const handleProceedToCheckout = () => {
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      navigate('/order-history', { 
        state: { 
          message: 'Order placed successfully!',
          orderTotal: total 
        }
      });
    }, 2000);
  };

  const handleRequestBulkQuote = (bulkRequest) => {
    console.log('Bulk quote request:', bulkRequest);
    navigate('/rfq-request-form', {
      state: {
        bulkOrder: true,
        items: bulkRequest?.items,
        requirements: bulkRequest?.requirements
      }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MainNavigation user={user} />
        <div className="pt-16">
          <div className="container mx-auto px-4 py-8">
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <Icon name="Loader2" size={48} className="mx-auto text-primary animate-spin mb-4" />
                <p className="text-muted-foreground">Loading your cart...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MainNavigation user={user} />
      <div className="pt-16">
        <div className="container mx-auto px-4 py-8">
          <BreadcrumbNavigation />
          
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Shopping Cart</h1>
              <p className="text-muted-foreground">
                Review your selected ICT solutions before checkout
              </p>
            </div>
            
            {cartItems?.length > 0 && (
              <div className="mt-4 lg:mt-0 flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  {cartItems?.length} item{cartItems?.length !== 1 ? 's' : ''} in cart
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowSavedItems(!showSavedItems)}
                  className="flex items-center gap-2"
                >
                  <Icon name="Heart" size={16} />
                  Saved Items ({savedItems?.length})
                </Button>
              </div>
            )}
          </div>

          {cartItems?.length === 0 ? (
            <EmptyCart />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {/* Bulk Order Section */}
                <BulkOrderSection
                  cartItems={cartItems}
                  onRequestBulkQuote={handleRequestBulkQuote}
                />

                {/* Cart Items List */}
                <div className="space-y-4">
                  {cartItems?.map((item) => (
                    <CartItem
                      key={item?.id}
                      item={item}
                      onUpdateQuantity={handleUpdateQuantity}
                      onRemove={handleRemoveItem}
                      onSaveForLater={handleSaveForLater}
                    />
                  ))}
                </div>

                {/* Saved Items Section */}
                {showSavedItems && savedItems?.length > 0 && (
                  <SavedItems
                    savedItems={savedItems}
                    onMoveToCart={handleMoveToCart}
                    onRemoveFromSaved={handleRemoveFromSaved}
                  />
                )}

                {/* Trust Signals */}
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">
                    Why Choose TechTribes?
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <Icon name="Shield" size={20} className="text-success" />
                      <div>
                        <p className="font-medium text-foreground">Secure Payments</p>
                        <p className="text-sm text-muted-foreground">SSL encrypted transactions</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon name="Award" size={20} className="text-primary" />
                      <div>
                        <p className="font-medium text-foreground">Certified Experts</p>
                        <p className="text-sm text-muted-foreground">Industry-certified professionals</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon name="Headphones" size={20} className="text-accent" />
                      <div>
                        <p className="font-medium text-foreground">24/7 Support</p>
                        <p className="text-sm text-muted-foreground">Round-the-clock assistance</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <OrderSummary
                  subtotal={subtotal}
                  tax={tax}
                  shipping={shipping}
                  discount={discount}
                  total={total}
                  onApplyPromoCode={handleApplyPromoCode}
                  onProceedToCheckout={handleProceedToCheckout}
                  isProcessing={isProcessing}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShoppingCart;